import { loaders, ProjectFile, TaskFile, User } from 'entities';
import { useLang } from 'hooks/lang';

import { Card, Row, SideSheet, Skeleton, Tag, Tooltip } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { DownloadFileButton } from 'components/Buttons';
import { TruncateText } from 'components/TruncateText';
import { FileUpIcon } from 'components/Icons';

import { FIRST_VERSION } from 'utils/constants';
import { fixFileName, getFirstAndLastName } from 'utils/text';
import { dateDistanceToNow, formatPublishedAtDate } from 'utils/date';
import { useOnScreen } from 'hooks';

interface RenameTaskFileButtonProps {
  file: TaskFile | ProjectFile;
}

function VersionTag({ version }: { version: number }) {
  return <Tag name={`v${version}`} color="primary" type="outlined" round={false} />;
}

export function FileVersionTag({ file }: RenameTaskFileButtonProps) {
  const { t } = useLang();

  const hasHistory = file.version > FIRST_VERSION;

  const [shouldLoad, ref] = useOnScreen();

  const [historyFiles, { errorOrLoading }] = loaders.projects.taskFileHistory.useV2({
    defaultValue: [],

    blockLoad: !hasHistory || !shouldLoad,

    params: { fileId: file.id },
  });

  if (!hasHistory)
    return (
      <span>
        <VersionTag version={file.version} />
      </span>
    );

  const allFiles = [file, ...historyFiles];

  return (
    <SideSheet
      title={t('file-versions')}
      trigger={
        <span className="cursor-pointer transition-opacity hover:opacity-55">
          <VersionTag version={file.version} />
        </span>
      }
    >
      <span ref={ref} />

      {allFiles.map((historyFile) => (
        <Card
          key={historyFile.id}
          className="grid grid-cols-12 items-center gap-4 text-foreground/50"
        >
          <Row gap="2" className="col-span-9 grow text-primary">
            <FileUpIcon />

            <TruncateText text={fixFileName(historyFile.name)} max={45} className="font-medium" />
          </Row>

          <Row gap="2" justifyContent="flex-end" className="col-span-3">
            <DownloadFileButton size={14} {...historyFile} />

            <VersionTag version={historyFile.version} />
          </Row>

          <span className="col-span-12 break-all text-sm italic">{historyFile.filename}</span>

          <Row gap="2" className="col-span-6">
            <UserAvatar
              user={historyFile.uploadedBy as User}
              className="text-xs phone:!h-8 phone:!w-8"
              size={20}
            />

            <span className="text-sm font-medium text-foreground">
              {getFirstAndLastName((historyFile.uploadedBy as User).name)}
            </span>
          </Row>

          <Tooltip content={formatPublishedAtDate(historyFile.uploadedAt)}>
            <span className="col-span-6 text-end text-xs font-medium">
              {dateDistanceToNow(historyFile.uploadedAt)}
            </span>
          </Tooltip>
        </Card>
      ))}

      {errorOrLoading && <Skeleton wrapMultiple={false} width="100%" height={50} quantity={3} />}
    </SideSheet>
  );
}
